import { z } from 'zod';
import {
  ChartAntiCd38SplitSchema,
  ChartArchetypeDistributionSchema,
  ChartDecisionMakingStyleSchema,
  ChartDeescalationOrderSchema,
  ChartEndpointImportanceSchema,
  ChartPatientMixSchema,
  ChartRegimenChoiceDriversSchema,
  ChartRegimenTypeMixSchema,
  ChartTolerabilityRankingSchema,
  ChartTopConstraintsSchema,
  ChartTopInformationSourcesSchema,
} from './segment-chart-data.js';
import { SegmentCardSchema } from './segment-card.js';
import {
  EXECUTIVE_SUMMARY_VERSION,
  ExecutiveSummaryCitationSchema,
  ExecutiveSummaryDataPointSchema,
  ExecutiveSummaryStatusSchema,
} from './segment-executive-summary.js';

// Cross-segment chart value: a composite shape that preserves each segment's
// original slice array so the UI can render grouped bars without re-querying
// per-segment rows. `axisLabels` is the canonical ordered category axis
// across all segments (union of labels, ordered by aggregate salience).
export const ComparisonChartPerSegmentSchema = z.object({
  segmentName: z.string().min(1),
  hcpCount: z.number().int().nonnegative(),
  slices: z.array(
    z.object({
      label: z.string(),
      count: z.number().int().nonnegative(),
      percent: z.number(),
    })
  ),
});

export const ComparisonChartValueSchema = z.object({
  hcpCount: z.number().int().nonnegative(),
  generatedAt: z.string(),
  axisLabels: z.array(z.string()),
  perSegment: z.array(ComparisonChartPerSegmentSchema),
});

export type ComparisonChartValue = z.infer<typeof ComparisonChartValueSchema>;
export type ComparisonChartPerSegment = z.infer<typeof ComparisonChartPerSegmentSchema>;

// Comparison citations carry the same shape as single-segment citations, plus
// the display name of the segment the citation originated from. The `source_id`
// itself is encoded as `{segmentSlug}:{originalSourceId}` and resolved to the
// display name by the server before the payload reaches the client.
export const ComparisonExecutiveSummaryCitationSchema = ExecutiveSummaryCitationSchema.extend({
  segment_name: z.string().min(1),
});

export type ComparisonExecutiveSummaryCitation = z.infer<
  typeof ComparisonExecutiveSummaryCitationSchema
>;

export const ComparisonExecutiveSummaryPayloadSchema = z.object({
  version: z.literal(EXECUTIVE_SUMMARY_VERSION),
  generated_at: z.string(),
  model: z.string().min(1),
  body_markdown: z.string().min(1),
  citations: z.array(ComparisonExecutiveSummaryCitationSchema),
  segment_names: z.array(z.string().min(1)),
});

export type ComparisonExecutiveSummaryPayload = z.infer<
  typeof ComparisonExecutiveSummaryPayloadSchema
>;

const SegmentCardColumnSchema = SegmentCardSchema.nullable().optional();

export const SegmentComparisonAggregatedDataSchema = z.object({
  id: z.number().int().nonnegative(),
  segmentNamesKey: z.string().min(1),
  segmentNames: z.array(z.string().min(1)).min(2),
  segmentationRunId: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
  coreIdentityPracticeContext: z.string().nullable().optional(),
  treatmentPhilosophyArchetype: z.string().nullable().optional(),
  regimenChoicePatterns1l: z.string().nullable().optional(),
  durationDeescalationSequencing: z.string().nullable().optional(),
  sharedDecisionMakingStyle: z.string().nullable().optional(),
  evidenceInformationPreferences: z.string().nullable().optional(),
  emotionalDriversFriction: z.string().nullable().optional(),
  cardPracticeModelRole: SegmentCardColumnSchema,
  cardPatientMix: SegmentCardColumnSchema,
  cardInstitutionalEnvironment: SegmentCardColumnSchema,
  cardArchetype: SegmentCardColumnSchema,
  cardRiskBenefitStance: SegmentCardColumnSchema,
  cardEvidenceOrientation: SegmentCardColumnSchema,
  card1lIntensityRegimen: SegmentCardColumnSchema,
  cardAntiCd38Behavior: SegmentCardColumnSchema,
  cardDurationSequencing: SegmentCardColumnSchema,
  cardPatientDecisionStyle: SegmentCardColumnSchema,
  cardInformationPreferences: SegmentCardColumnSchema,
  cardConstraints: SegmentCardColumnSchema,
  cardMotivatorsChangeTriggers: SegmentCardColumnSchema,
  chartArchetypeDistribution: ComparisonChartValueSchema.nullable().optional(),
  chartRegimenTypeMix: ComparisonChartValueSchema.nullable().optional(),
  chartAntiCd38Split: ComparisonChartValueSchema.nullable().optional(),
  chartDecisionMakingStyle: ComparisonChartValueSchema.nullable().optional(),
  chartTopConstraints: ComparisonChartValueSchema.nullable().optional(),
  chartPatientMix: ComparisonChartValueSchema.nullable().optional(),
  chartTolerabilityRanking: ComparisonChartValueSchema.nullable().optional(),
  chartEndpointImportance: ComparisonChartValueSchema.nullable().optional(),
  chartDeescalationOrder: ComparisonChartValueSchema.nullable().optional(),
  chartTopInformationSources: ComparisonChartValueSchema.nullable().optional(),
  chartRegimenChoiceDrivers: ComparisonChartValueSchema.nullable().optional(),
  executiveSummary: ComparisonExecutiveSummaryPayloadSchema.nullable().optional(),
  executiveSummaryStatus: ExecutiveSummaryStatusSchema.optional(),
});

export type SegmentComparisonAggregatedData = z.infer<
  typeof SegmentComparisonAggregatedDataSchema
>;

// Canonical composite key helpers. Unordered: [A, B] and [B, A] normalize to
// the same key. Scoped by segmentationRunId so the same name set in two
// different runs resolves to two distinct rows. The ordered names are
// preserved separately on the row for display and for regeneration context.
//
// Delimiter choice: `|` (between names) and `::` (between run id and name
// set) are safe for the current comparison-eligible names; any future
// delimiter change must be re-audited against COMPARISON_ELIGIBLE_SEGMENTS.
export function canonicalizeSegmentNamesKey(
  segmentationRunId: number,
  names: readonly string[]
): string {
  const deduped = Array.from(new Set(names.map((n) => n.trim()).filter((n) => n.length > 0)));
  const joined = deduped
    .map((n) => n.toLowerCase())
    .sort((a, b) => a.localeCompare(b))
    .join('|');
  return `${segmentationRunId}::${joined}`;
}

export const MIN_COMPARISON_SEGMENTS = 2;
export const MAX_COMPARISON_SEGMENTS = 6;

export namespace SegmentComparisonAPI {
  const segmentNamesQuerySchema = z
    .string()
    .min(1)
    .transform((s) => s.split(',').map((n) => n.trim()).filter((n) => n.length > 0))
    .pipe(
      z
        .array(z.string().min(1))
        .min(MIN_COMPARISON_SEGMENTS)
        .max(MAX_COMPARISON_SEGMENTS)
    );

  export namespace Get {
    export const requestQuerySchema = z.object({
      segments: segmentNamesQuerySchema,
      segmentationRunId: z.coerce.number().int().positive(),
    });

    export const responseBodySchema = z.object({
      item: SegmentComparisonAggregatedDataSchema.nullable(),
    });

    export type Response = z.infer<typeof responseBodySchema>;
  }

  export namespace Generate {
    export const requestBodySchema = z.object({
      segmentNames: z
        .array(z.string().min(1))
        .min(MIN_COMPARISON_SEGMENTS)
        .max(MAX_COMPARISON_SEGMENTS),
      segmentationRunId: z.number().int().positive(),
    });

    export const responseBodySchema = z.object({
      item: SegmentComparisonAggregatedDataSchema,
    });

    export type Response = z.infer<typeof responseBodySchema>;
  }

  export namespace GetCardCitations {
    export const requestQuerySchema = z.object({
      segments: segmentNamesQuerySchema,
      segmentationRunId: z.coerce.number().int().positive(),
      cardKey: z.string().min(1),
    });

    const citationTranscriptSchema = z.object({
      transcriptId: z.number(),
      transcriptName: z.string(),
    });

    const citationSectionSchema = z.object({
      sectionKey: z.string(),
      sectionTitle: z.string(),
      transcripts: z.array(citationTranscriptSchema),
    });

    const perSegmentSchema = z.object({
      segmentName: z.string().min(1),
      sections: z.array(citationSectionSchema),
    });

    export const responseBodySchema = z.object({
      perSegment: z.array(perSegmentSchema),
    });

    export type CitationTranscript = z.infer<typeof citationTranscriptSchema>;
    export type CitationSection = z.infer<typeof citationSectionSchema>;
    export type PerSegmentCitations = z.infer<typeof perSegmentSchema>;
    export type Response = z.infer<typeof responseBodySchema>;
  }
}

// Re-export so client code can rely on a single import.
export { ExecutiveSummaryDataPointSchema };
export type { ExecutiveSummaryDataPoint } from './segment-executive-summary.js';
